import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { KpiGrid, KpiCard, StatusChip } from "@/components/kcc/Kpi";
import { KccAreaChart, KccLineChart, KccBarChart } from "@/components/kcc/Charts";
import { Panel, ModuleHeader } from "@/components/kcc/Panel";
import { AIAdvisor } from "@/components/kcc/AIAdvisor";
import { DataTable, statusToChip } from "@/components/kcc/DataTable";
import { Link } from "react-router-dom";
import { TrendingUp, ChevronRight, Factory, Mountain, ShieldCheck, Waves, ArrowRight, Sparkles } from "lucide-react";

const IMG = "https://images.pexels.com/photos/32845695/pexels-photo-32845695.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function ExecutiveCommandCentre() {
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const [notif, setNotif] = useState({ items: [], count: 0 });

    useEffect(() => {
        api.get("/executive/kpis").then(({ data }) => setData(data));
        api.get("/notifications").then(({ data }) => setNotif(data));
    }, []);

    const trends = data?.trends || [];
    const chartData = trends.map((t) => ({
        date: t.period.slice(2),
        copper: t.copper_tonnes,
        cobalt: t.cobalt_tonnes,
        revenue: t.revenue_musd,
        ebitda: t.ebitda_musd,
        cost: t.cash_cost_usd_lb,
        trir: t.trir,
    }));

    const roleLabel = user?.title || user?.role || "Executive";

    return (
        <div className="space-y-6">
            <ModuleHeader
                kicker="Executive Command Centre"
                title={`Good day, ${user?.name?.split(" ")[0] || "Executive"}`}
                description={`One secure operational picture for Kamoto Copper Company. Live production, safety, environmental, financial and executive AI intelligence — tailored for ${roleLabel}.`}
                image={IMG}
                testid="exec-header"
                actions={
                    <>
                        <Link to="/ai" className="inline-flex items-center gap-2 bg-kcc-purple/15 hover:bg-kcc-purple/25 border border-kcc-purple/40 text-white rounded-md px-4 py-2 text-sm">
                            <Sparkles className="w-4 h-4 text-kcc-purple" /> Executive AI briefing
                        </Link>
                        <Link to="/board" className="inline-flex items-center gap-2 bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 text-white rounded-md px-4 py-2 text-sm">
                            Board Portal <ArrowRight className="w-4 h-4" />
                        </Link>
                    </>
                }
            />

            <KpiGrid kpis={data?.kpis || []} accent="copper" cols={4} />

            <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
                <Panel title="Copper & Cobalt Production" subtitle="30-month trend · monthly tonnes" accent="copper" className="xl:col-span-2" testid="exec-production-chart">
                    <KccLineChart
                        data={chartData}
                        series={[
                            { key: "copper", label: "Copper (t)", color: "#e88c4a" },
                            { key: "cobalt", label: "Cobalt (t)", color: "#4f8bd9" },
                        ]}
                        height={300}
                        xKey="date"
                    />
                </Panel>

                <Panel title="Revenue & EBITDA" subtitle="USD millions · monthly" accent="cobalt" testid="exec-revenue-chart">
                    <KccBarChart data={chartData.slice(-12)} dataKey="revenue" label="Revenue $M" color="#2d68c4" xKey="date" height={300} />
                </Panel>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
                <Panel title="C1 Cash Cost" subtitle="USD / lb Cu" accent="amber" testid="exec-cost-chart">
                    <KccAreaChart data={chartData} dataKey="cost" label="Cost $/lb" color="#f59e0b" xKey="date" height={220} />
                </Panel>
                <Panel title="Safety TRIR" subtitle="Per 200,000 hours worked · trending" accent="green" testid="exec-trir-chart">
                    <KccAreaChart data={chartData} dataKey="trir" label="TRIR" color="#22c55e" xKey="date" height={220} />
                </Panel>
                <Panel title="Live escalations" subtitle="Cross-module notifications" accent="red" testid="exec-escalations">
                    <div className="space-y-2">
                        {notif.items.slice(0, 6).map((n) => (
                            <div key={n.id} className="flex items-start gap-3 p-2.5 rounded border border-white/5 hover:border-white/15 bg-white/[0.02]">
                                <StatusChip status={n.severity === "critical" ? "critical" : n.severity === "warning" ? "warning" : "info"}>{n.module}</StatusChip>
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm text-white truncate">{n.title}</div>
                                    <div className="text-[11px] kcc-text-silver truncate">{n.detail}</div>
                                </div>
                            </div>
                        ))}
                        {!notif.items.length && <div className="text-sm kcc-text-dim italic p-4 text-center">All quiet across the enterprise.</div>}
                    </div>
                </Panel>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
                <div className="xl:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Link to="/mining" className="kcc-panel kcc-copper-accent p-5 hover:border-white/25 transition-colors group" data-testid="link-mining">
                        <Mountain className="w-6 h-6 text-kcc-copper-light" />
                        <div className="font-display text-lg font-semibold mt-3 text-white">Mining360</div>
                        <div className="kcc-text-silver text-sm">Open pit + underground · fleet · dispatch · drill & blast</div>
                        <div className="mt-3 flex items-center gap-1 text-xs text-kcc-copper-light group-hover:gap-2 transition-all">Open <ChevronRight className="w-3.5 h-3.5" /></div>
                    </Link>
                    <Link to="/plant" className="kcc-panel kcc-cobalt-accent p-5 hover:border-white/25 transition-colors group" data-testid="link-plant">
                        <Factory className="w-6 h-6 text-kcc-cobalt-light" />
                        <div className="font-display text-lg font-semibold mt-3 text-white">Plant360</div>
                        <div className="kcc-text-silver text-sm">Concentrator · hydromet · refinery · Cu cathode + cobalt</div>
                        <div className="mt-3 flex items-center gap-1 text-xs text-kcc-cobalt-light group-hover:gap-2 transition-all">Open <ChevronRight className="w-3.5 h-3.5" /></div>
                    </Link>
                    <Link to="/safety" className="kcc-panel kcc-green-accent p-5 hover:border-white/25 transition-colors group" data-testid="link-safety">
                        <ShieldCheck className="w-6 h-6 text-kcc-green" />
                        <div className="font-display text-lg font-semibold mt-3 text-white">Safety360</div>
                        <div className="kcc-text-silver text-sm">Incidents · permits · fatal-risk controls · investigations</div>
                        <div className="mt-3 flex items-center gap-1 text-xs text-kcc-green group-hover:gap-2 transition-all">Open <ChevronRight className="w-3.5 h-3.5" /></div>
                    </Link>
                    <Link to="/tailings" className="kcc-panel kcc-red-accent p-5 hover:border-white/25 transition-colors group" data-testid="link-tailings">
                        <Waves className="w-6 h-6 text-kcc-red" />
                        <div className="font-display text-lg font-semibold mt-3 text-white">Tailings360</div>
                        <div className="kcc-text-silver text-sm">Facility register · piezometers · TARP · independent review</div>
                        <div className="mt-3 flex items-center gap-1 text-xs text-kcc-red group-hover:gap-2 transition-all">Open <ChevronRight className="w-3.5 h-3.5" /></div>
                    </Link>
                </div>
                <Panel title="AI Executive Advisor" subtitle="Ask the KCC360 AI anything about the enterprise" accent="purple" className="min-h-[500px] flex flex-col" testid="exec-ai">
                    <div className="flex-1 -m-5">
                        <AIAdvisor module="executive" embedded />
                    </div>
                </Panel>
            </div>
        </div>
    );
}
